import { NotificationType } from '@electricjs/core_entity-client';

import { Flex } from '@electricjs/arc';
import { Trans } from 'react-i18next';

import i18n from '@/i18n';

import { useGetRequestData } from '@/hooks/useGetRequestData';
import { EmployeeDirectoryListing } from '@/types/employees';
import { Request, RequestType } from '@/types/requests';

import { EmailDetail } from '../EmailDetail';
import { GroupDetail } from '../GroupDetail';
import { NotificationIcon } from '../NotificationIcon';
import { NotificationItemHeader } from '../NotificationItemHeader';
import { RequestActions } from '../RequestActions/RequestActions';
import { NotificationItemProps } from '../types';
import { useGetRequestHardwareData } from '@/hooks/useGetRequestHardwareData';
import { getDisplayFullName } from 'common/utils/getFullName';

const getTitle = (employee?: EmployeeDirectoryListing) => {
  const employeeName = employee
    ? getDisplayFullName(employee)
    : i18n.t('common:notificationItems.theNewHire');

  return (
    <Trans
      i18nKey="notificationItems.hardwareRequest.title"
      ns="common"
      values={{ employeeName }}
      components={{ bold: <b /> }}
    />
  );
};

type HardwareRequestActionsColumnProps = {
  request?: Request;
  requestType?: RequestType;
  isLoading: boolean;
};

export const HardwareRequestActionsColumn = ({
  request,
  requestType,
  isLoading,
}: HardwareRequestActionsColumnProps) => {
  return (
    <RequestActions
      loading={isLoading}
      request={request}
      requestType={requestType}
      isDropDownMenu={false}
    />
  );
};

export const HardwareRequestDetailsColumns = ({
  notification,
}: NotificationItemProps) => {
  const { data: requestData, isLoading } = useGetRequestHardwareData({
    requestId: notification?.data?.request_id ?? null,
  });

  const employeeGroups = requestData.employeeGroups;
  const employeeEmail = requestData?.employee?.email;

  return (
    <Flex flexDirection="column">
      <GroupDetail isLoading={isLoading} groups={employeeGroups} />
      <EmailDetail isLoading={isLoading} email={employeeEmail} />
    </Flex>
  );
};

const HardwareRequestNotificationItem = ({
  notification,
  hideDetails,
}: NotificationItemProps) => {
  const { data: requestData, isLoading } = useGetRequestData({
    requestId: notification?.data?.request_id ?? null,
    options: {
      includeEmployee: true,
      includeEmployeeGroups: true,
    },
  });

  const employee = requestData?.employee;
  const employeeGroups = requestData?.employeeGroups;
  const employeeEmail = employee?.email;

  const getLabel = () => {
    switch (notification.type) {
      case NotificationType.RequestAssigned:
        return i18n.t('common:notificationItems.labels.requestAssigned');

      case NotificationType.RequestCompleted:
        return i18n.t('common:notificationItems.labels.requestCompleted');
      default:
        return '';
    }
  };

  const getIcon = () => (
    <NotificationIcon
      isNotificationUnseen={!notification.seenAt}
      loading={isLoading}
      icon="laptop"
    />
  );

  return (
    <NotificationItemHeader
      notification={notification}
      isLoading={isLoading}
      title={getTitle(employee)}
      icon={getIcon()}
      label={getLabel()}>
      {!hideDetails ? (
        <>
          <GroupDetail isLoading={isLoading} groups={employeeGroups} />
          <EmailDetail isLoading={isLoading} email={employeeEmail} />
          <RequestActions
            loading={isLoading}
            request={requestData.request}
            requestType={requestData.requestType}
          />
        </>
      ) : null}
    </NotificationItemHeader>
  );
};

export default HardwareRequestNotificationItem;
